import { useState, useRef, MouseEvent, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FormErrors, FormState, Mode, Specification, FAQ, ApplicationStoryFormState } from "../../types/product";
import { EMPTY_FORM, MOCK_PRODUCT } from "../../constants/product";
import { hydrateProduct, uid } from "../../helpers/product";
import Tag from "../../components/common/UI/Tag";
import Field from "../../components/common/Form/Field";
import Input from "../../components/common/Form/Input";
import Textarea from "../../components/common/Form/Textarea";
import SeoScorePanel from "../../components/pages/products/Management/SeoScorePanel";
import SerpPreviewPanel from "../../components/pages/products/Management/SerpPreviewPanel";
import QuickStatsPanel from "../../components/pages/products/Management/QuickStatsPanel";
import Section from "../../components/common/UI/Section";
import SpecRow from "../../components/common/UI/SpaceRow";
import { createProduct, fetchProduct, updateProduct, uploadProductTds } from "../../services/productService";

// Helper components
function TagInput({
    values,
    onChange,
    placeholder,
}: {
    values: string[];
    onChange: (v: string[]) => void;
    placeholder?: string;
}) {
    const [input, setInput] = useState("");
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            const val = input.trim();
            if (val && !values.includes(val)) {
                onChange([...values, val]);
            }
            setInput("");
        }
    };
    return (
        <div style={{
            display: "flex", flexWrap: "wrap", gap: 6, padding: "8px 12px",
            background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", borderRadius: 8,
            minHeight: 38, alignItems: "center"
        }}>
            {values.map((tag) => (
                <span key={tag} style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    background: "var(--or-dim)", color: "var(--or)", border: "1px solid rgba(191,61,12,0.2)",
                    borderRadius: 4, padding: "2px 6px", fontSize: 12, fontWeight: 500
                }}>
                    {tag}
                    <button type="button" onClick={() => onChange(values.filter(t => t !== tag))} style={{
                        background: "none", border: "none", color: "var(--or)", cursor: "pointer",
                        padding: 0, fontSize: 12, lineHeight: 1, display: "flex", alignItems: "center"
                    }}>×</button>
                </span>
            ))}
            <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={values.length === 0 ? placeholder : ""}
                style={{
                    border: "none", background: "transparent", color: "var(--text)",
                    outline: "none", fontSize: 13, flex: "1 1 80px", fontFamily: "'DM Sans', sans-serif"
                }}
            />
        </div>
    );
}

function CollapsibleSection({
    title,
    desc,
    icon,
    open,
    onToggle,
    badge,
    children,
}: {
    title: string;
    desc?: string;
    icon?: string;
    open: boolean;
    onToggle: () => void;
    badge?: string;
    children: React.ReactNode;
}) {
    return (
        <div style={{
            background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14,
            overflow: "hidden", marginBottom: 16, transition: "all 0.2s"
        }}>
            <div
                onClick={onToggle}
                style={{
                    display: "flex", alignItems: "center", gap: 12, padding: "16px 20px",
                    background: "var(--section-header)", cursor: "pointer", userSelect: "none"
                }}
            >
                {icon && <span style={{ fontSize: 18 }}>{icon}</span>}
                <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", display: "block" }}>{title}</span>
                    {desc && <span style={{ fontSize: 11, color: "var(--text3)", marginTop: 2, display: "block" }}>{desc}</span>}
                </div>
                {badge && (
                    <span style={{
                        fontSize: 10, fontWeight: 600, background: "var(--or-dim)", color: "var(--or)",
                        borderRadius: 20, padding: "2px 8px"
                    }}>{badge}</span>
                )}
                <span style={{
                    fontSize: 12, color: "var(--text3)", transition: "transform 0.2s",
                    transform: open ? "rotate(180deg)" : "rotate(0deg)", marginLeft: 8
                }}>▼</span>
            </div>
            {open && (
                <div style={{
                    padding: 20, display: "flex", flexDirection: "column", gap: 18,
                    borderTop: "1px solid var(--border)", animation: "fadeSlideIn 0.25s ease"
                }}>
                    {children}
                </div>
            )}
        </div>
    );
}

export default function ManageProduct() {
    const location = useLocation();
    const navigate = useNavigate();
    const editId: string | number | undefined = (location.state as { id?: string | number } | null)?.id;

    const [mode, setMode] = useState<Mode>(editId ? "edit" : "add");
    const [form, setForm] = useState<FormState>(EMPTY_FORM());
    const [loading, setLoading] = useState(!!editId);
    const [submitting, setSubmitting] = useState(false);
    const [tdsUploading, setTdsUploading] = useState(false);
    const [saved, setSaved] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [errors, setErrors] = useState<FormErrors>({});
    const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Accordion expand/collapse state
    const [open, setOpen] = useState<Record<number, boolean>>({
        1: false, // Product Information (Sub Information)
        2: true,  // Tab Identity (Tab Styling & Identity)
        3: false, // Specifications
        4: false, // Packaging & Certs
        5: false, // Target Applications
        6: false, // SEO Block
    });

    // Load product for edit
    useEffect(() => {
        if (!editId) return;
        setLoading(true);
        fetchProduct(editId)
            .then((product) => {
                setForm(hydrateProduct(product));
                setMode("edit");
            })
            .catch(() => setSaveError("Failed to load product data."))
            .finally(() => setLoading(false));
    }, [editId]);

    const set = <K extends keyof FormState>(key: K) => (val: FormState[K]) =>
        setForm((f) => {
            const next = { ...f, [key]: val };
            if (key === "metaTitle") next.meta_title = val as string;
            if (key === "metaDescription") next.meta_description = val as string;
            if (key === "meta_title") next.metaTitle = val as string;
            if (key === "meta_description") next.metaDescription = val as string;
            return next;
        });

    const update = (fields: Partial<FormState>) => {
        setForm((f) => {
            const next = { ...f, ...fields };
            if ("metaTitle" in fields)        next.meta_title = fields.metaTitle || "";
            if ("metaDescription" in fields)  next.meta_description = fields.metaDescription || "";
            if ("meta_title" in fields)       next.metaTitle = fields.meta_title || "";
            if ("meta_description" in fields) next.metaDescription = fields.meta_description || "";
            return next;
        });
    };

    const clearError = (key: keyof FormErrors) =>
        setErrors((prev) => ({ ...prev, [key]: null }));

    const validate = (): boolean => {
        const e: FormErrors = {};
        if (!form.product_description.trim()) e.product_description = "Description is required.";
        const titleVal = (form.metaTitle || form.meta_title || "").trim();
        if (!titleVal)                        e.meta_title          = "Meta title is required.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = async () => {
        console.log("Saving product:", form);
        if (!validate()) return;
        setSaveError(null);
        setSubmitting(true);

        const payload = {
            product_description:    form.product_description,
            meta_title:             form.meta_title,
            meta_description:       form.meta_description,
            metaTitle:              form.metaTitle,
            metaDescription:        form.metaDescription,
            status:                 form.status,
            product_specifications: form.product_specifications.map(({ key, value }) => ({ key, value })),
            
            // Layout & identity fields
            product_key:            form.product_key,
            tab_name:               form.tab_name,
            tab_sub:                form.tab_sub,
            ec:                     form.ec,
            name:                   form.name,
            name_em_start:          Number(form.name_em_start),
            tagline:                form.tagline,
            formats:                form.formats,
            certs:                  form.certs,
            cta_copy:               form.cta_copy,
            applications:           form.applications.map((app) => ({
                kicker: app.kicker,
                heading: app.heading,
                headingEm: Number(app.headingEm),
                body: app.body,
                transformFrom: { label: app.transformFrom.label, text: app.transformFrom.text },
                transformTo: { label: app.transformTo.label, text: app.transformTo.text },
                metrics: app.metrics.map(({ num, label }) => ({ num, label })),
                buyersLabel: app.buyersLabel,
                buyers: app.buyers,
                flowLabel: app.flowLabel,
                flowSteps: app.flowSteps.map(({ title, desc }) => ({ title, desc })),
                flowResultText: app.flowResultText,
            })),

            // SEO fields
            focusKeyword:           form.focusKeyword,
            secondaryKeywords:      form.secondaryKeywords,
            faqs:                   (form.faqs || []).map(({ question, answer }) => ({ question, answer })),
            tds_url:                form.tds_url || null,
        };
        console.log("Prepared payload for submission:", payload);

        try {
            console.log("Submitting payload:", mode);
            if (mode === "edit" && editId) {
                await updateProduct(editId, payload);
            } else {
                await createProduct(payload);
            }

            if (saveTimer.current) clearTimeout(saveTimer.current);
            setSaved(true);
            saveTimer.current = setTimeout(() => {
                setSaved(false);
                navigate("/content/products");
            }, 2000);
        } catch (err: unknown) {
            setSaveError(err instanceof Error ? err.message : "Save failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleReset = () => { setForm(EMPTY_FORM()); setErrors({}); setMode("add"); setSaveError(null); };
    const handleLoadDemo = () => { setForm(hydrateProduct(MOCK_PRODUCT)); setMode("edit"); setErrors({}); };

    const addSpec = () =>
        setForm((f) => ({ ...f, product_specifications: [...f.product_specifications, { id: uid(), key: "", value: "" }] }));

    const updateSpec = (id: string, updated: Specification) =>
        setForm((f) => ({ ...f, product_specifications: f.product_specifications.map((s) => s.id === id ? { ...updated, id } : s) }));

    const removeSpec = (id: string) =>
        setForm((f) => ({ ...f, product_specifications: f.product_specifications.filter((s) => s.id !== id) }));

    // Dynamic Applications list controllers
    const addApplication = () => {
        const newApp: ApplicationStoryFormState = {
            id: uid(),
            kicker: "Application — Name",
            heading: ["", ""],
            headingEm: 1,
            body: "",
            transformFrom: { label: "Input", text: "" },
            transformTo: { label: "Output", text: "" },
            metrics: [{ num: "", label: "" }],
            buyersLabel: "Who buys this",
            buyers: [],
            flowLabel: "Process steps",
            flowSteps: [{ title: "", desc: "" }],
            flowResultText: "",
        };
        setForm((f) => ({ ...f, applications: [...f.applications, newApp] }));
    };

    const removeApplication = (appId: string) => {
        setForm((f) => ({ ...f, applications: f.applications.filter((app) => app.id !== appId) }));
    };

    const updateApplication = (appId: string, fields: Partial<ApplicationStoryFormState>) => {
        setForm((f) => ({
            ...f,
            applications: f.applications.map((app) => (app.id === appId ? { ...app, ...fields } : app)),
        }));
    };

    // SEO variables setup for the user-requested HTML block template
    const p = form;
    const isAi = (field: string) => false;
    const inputClass = (field: string) => "pf-input";

    if (loading) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh", color: "var(--text3)", fontSize: 14 }}>
                Loading product…
            </div>
        );
    }

    return (
        <>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        body { margin: 0; font-family: 'DM Sans', sans-serif; }
        input::placeholder, textarea::placeholder { color: var(--text3); }
        input, textarea { color: var(--text); }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }
        @keyframes fadeSlideIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes toastIn     { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
        .pf-layout {
          display: grid;
          grid-template-columns: 1fr 310px;
          gap: 24px;
          align-items: start;
        }
        .pf-sidebar { display: flex; flex-direction: column; gap: 14px; position: sticky; top: 24px; }
        @media (max-width: 1080px) { .pf-layout { grid-template-columns: 1fr; } .pf-sidebar { position: static; } }
        @media (max-width: 480px) { .manage-prod-header { display: block !important; } }
        select.pf-select {
          width: 100%;
          background: var(--input-bg) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23888888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E") no-repeat right 14px center;
          background-size: 16px;
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 10px 36px 10px 14px;
          font-size: 14px;
          color: var(--text);
          outline: none;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          appearance: none;
        }
        .pf-input, .pf-textarea {
          width: 100%;
          background: var(--input-bg);
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 10px 14px;
          font-size: 14px;
          color: var(--text);
          outline: none;
          font-family: 'DM Sans', sans-serif;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .pf-input:focus, .pf-textarea:focus {
          border-color: var(--or);
          box-shadow: 0 0 0 2px rgba(191,61,12,0.15);
        }
        .subgroup-title {
          font-size: 12px;
          font-weight: 600;
          color: var(--text2);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-top: 18px;
          margin-bottom: 8px;
        }
        .field-row {
          display: grid;
          gap: 12px;
        }
        .cols-2 { grid-template-columns: 1fr 1fr; }
        .cols-1-2 { grid-template-columns: 1fr 2fr; }
        @media (max-width: 600px) {
          .cols-2, .cols-1-2 { grid-template-columns: 1fr; }
        }
        .repeater { display: grid; gap: 12px; }
        .rep-row {
          background: rgba(255,255,255,0.01);
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 14px;
          display: grid;
          gap: 12px;
          position: relative;
        }
        .rep-add {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          width: 100%;
          padding: 10px;
          border-radius: 8px;
          border: 1px dashed rgba(191,61,12,0.3);
          background: transparent;
          color: var(--or);
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          transition: all 0.15s;
        }
        .rep-add:hover {
          background: var(--or-dim);
          border-style: solid;
        }
        .x {
          background: none;
          border: none;
          color: var(--text3);
          font-size: 20px;
          cursor: pointer;
          line-height: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          transition: all 0.12s;
        }
        .x:hover {
          background: rgba(224,82,82,0.12);
          color: #e05252;
        }
      `}</style>

            <div style={{ minHeight: "100vh", padding: "28px" }}>

                {/* Success toast */}
                {saved && (
                    <div style={{
                        position: "fixed", top: 20, right: 20, zIndex: 999,
                        background: "#1a2e1a", border: "1px solid rgba(90,200,90,0.25)",
                        borderRadius: 10, padding: "12px 18px",
                        display: "flex", alignItems: "center", gap: 10,
                        animation: "toastIn 0.25s ease", boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                    }}>
                        <div style={{
                            width: 20, height: 20, borderRadius: "50%",
                            background: "rgba(90,200,90,0.2)", border: "1px solid rgba(90,200,90,0.4)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 12, color: "#6ddc6d",
                        }}>✓</div>
                        <span style={{ fontSize: 14, color: "#9be09b" }}>
                            Product {mode === "edit" ? "updated" : "published"} successfully
                        </span>
                    </div>
                )}

                {/* Error toast */}
                {saveError && (
                    <div style={{
                        position: "fixed", top: 20, right: 20, zIndex: 999,
                        background: "#2e1a1a", border: "1px solid rgba(224,82,82,0.25)",
                        borderRadius: 10, padding: "12px 18px",
                        display: "flex", alignItems: "center", gap: 10,
                        animation: "toastIn 0.25s ease", boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                    }}>
                        <span style={{ fontSize: 14, color: "#e08b8b" }}>✕ {saveError}</span>
                        <button onClick={() => setSaveError(null)} style={{ background: "none", border: "none", color: "#e08b8b", cursor: "pointer", fontSize: 16, padding: 0, marginLeft: 4 }}>×</button>
                    </div>
                )}

                <div style={{ maxWidth: 1200, margin: "0 auto" }}>

                    {/* Page header */}
                    <div style={{
                        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                        marginBottom: 24, animation: "fadeSlideIn 0.3s ease",
                    }} className="manage-prod-header">
                        <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                                <Tag>{mode === "edit" ? "Editing" : "New Product"}</Tag>
                            </div>
                            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "var(--text)", letterSpacing: "-0.4px" }}>
                                {mode === "edit" ? (form.tab_name || "Edit Product") : "Add Product"}
                            </h1>
                            <p style={{ margin: "4px 0 0", fontSize: 14, color: "var(--text3)" }}>
                                {mode === "edit" ? "Update the fields below and save your changes." : "Fill in the details to create a new product."}
                            </p>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexShrink: 0, marginTop: 10 }}>
                            {/* <button onClick={handleReset} style={{
                                fontSize: 13, padding: "8px 14px", borderRadius: 8,
                                border: "1px solid var(--border)", background: "transparent",
                                color: "var(--text2)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                            }}>+ New</button> */}
                            {/* <button onClick={handleLoadDemo} style={{
                                fontSize: 13, padding: "8px 14px", borderRadius: 8,
                                border: "1px solid rgba(191,61,12,0.3)", background: "var(--or-dim)",
                                color: "var(--or)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                            }}>Load Demo</button> */}
                        </div>
                    </div>

                    {/* Two-column layout */}
                    <div className="pf-layout">

                        {/* Left column */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 16, animation: "fadeSlideIn 0.35s ease 0.05s both", minWidth: 0 }}>
                            {/* Section 1: Tab Styling & Identity */}
                            <CollapsibleSection
                                title="1. Product Tab Styling & Identity"
                                desc="Configure the look-and-feel of the product deck tab and titles"
                                open={open[2]}
                                onToggle={() => setOpen((o) => ({ ...o, 2: !o[2] }))}
                            >
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                    <Field label="Product Name" hint="Clean shortened name (e.g. Glucoamylase)">
                                        <input
                                            className="pf-input"
                                            value={form.tab_name}
                                            onChange={(e) => set("tab_name")(e.target.value)}
                                            placeholder="Glucoamylase"
                                        />
                                    </Field>
                                    <Field label="Product Sub Name" hint="Quick tags under tab (e.g. Starch · Bioethanol)">
                                        <input
                                            className="pf-input"
                                            value={form.tab_sub}
                                            onChange={(e) => set("tab_sub")(e.target.value)}
                                            placeholder="Starch · Bioethanol"
                                        />
                                    </Field>
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                                    <Field label="EC Classification Code" hint="Enzyme class code">
                                        <input
                                            className="pf-input"
                                            value={form.ec}
                                            onChange={(e) => set("ec")(e.target.value)}
                                            placeholder="Carbohydrase · EC 3.2.1.3"
                                        />
                                    </Field>
                                    <Field label="Multi-line Name Splitting" hint="Name split into parts (max 2)">
                                        <div style={{ display: "flex", gap: 8 }}>
                                            <input
                                                className="pf-input"
                                                value={form.name[0] || ""}
                                                onChange={(e) => set("name")([e.target.value, form.name[1] || ""])}
                                                placeholder="Part 1 (e.g. Gluco)"
                                            />
                                            <input
                                                className="pf-input"
                                                value={form.name[1] || ""}
                                                onChange={(e) => set("name")([form.name[0] || "", e.target.value])}
                                                placeholder="Part 2 (e.g. amylase)"
                                            />
                                        </div>
                                    </Field>
                                    <Field label="Emphasis Start Character Index" hint="Index to start styling italic">
                                        <input
                                            className="pf-input"
                                            type="number"
                                            value={form.name_em_start}
                                            onChange={(e) => set("name_em_start")(Number(e.target.value))}
                                            placeholder="5"
                                        />
                                    </Field>
                                </div>
                                <Field label="Short Tagline" hint="Appears inside card descriptions (max 150 chars)">
                                    <textarea
                                        className="pf-textarea"
                                        value={form.tagline}
                                        onChange={(e) => set("tagline")(e.target.value)}
                                        placeholder="Turns starch into fermentable sugars..."
                                        rows={2}
                                        maxLength={300}
                                    />
                                </Field>
                            </CollapsibleSection>

                            {/* Section 2: Product Information */}
                            <CollapsibleSection
                                title="2. Product Sub Information"
                                desc="Key identifiers and core details of the enzyme"
                                open={open[1]}
                                onToggle={() => setOpen((o) => ({ ...o, 1: !o[1] }))}
                            >
                                <Field label="Product Key/Slug" required hint="Unique URL key (lowercase)">
                                    <input
                                        className="pf-input"
                                        value={form.product_key}
                                        onChange={(e) => set("product_key")(e.target.value.toLowerCase().replace(/\s+/g, "-"))}
                                        placeholder="e.g. glucoamylase"
                                        maxLength={100}
                                    />
                                </Field>
                                <Field label="Product Description / Body" required hint="Primary details shown on the product tab">
                                    <textarea
                                        className="pf-textarea"
                                        value={form.product_description}
                                        onChange={(e) => { set("product_description")(e.target.value); clearError("product_description"); }}
                                        placeholder="Describe the product, its mechanism of action, and economic benefits…"
                                        rows={5} maxLength={2000}
                                    />
                                    {errors.product_description && <span style={{ fontSize: 12, color: "#e05252" }}>{errors.product_description}</span>}
                                </Field>
                                <Field label="TDS Document (PDF)" hint="Upload Technical Data Sheet for the website (PDF only)">
                                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                        {form.tds_url ? (
                                            <div style={{
                                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                                background: "var(--input-bg)", border: "1px solid var(--border)",
                                                borderRadius: 8, padding: "10px 14px"
                                            }}>
                                                <a href={form.tds_url} target="_blank" rel="noopener noreferrer" style={{
                                                    display: "flex", alignItems: "center", gap: 8,
                                                    color: "var(--or)", fontSize: 13, textDecoration: "none", fontWeight: 500
                                                }}>
                                                    <span>📄</span>
                                                    <span style={{ textDecoration: "underline" }}>Download Uploaded TDS PDF</span>
                                                </a>
                                                <button
                                                    type="button"
                                                    onClick={() => set("tds_url")("")}
                                                    style={{
                                                        background: "transparent", border: "none", color: "#e05252",
                                                        cursor: "pointer", fontSize: 12, fontWeight: 500,
                                                        fontFamily: "'DM Sans', sans-serif"
                                                    }}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ) : (
                                            <div style={{ position: "relative" }}>
                                                <input
                                                    type="file"
                                                    accept=".pdf"
                                                    onChange={async (e) => {
                                                        const file = e.target.files?.[0];
                                                        if (!file) return;
                                                        if (file.type !== "application/pdf") {
                                                            alert("Please select a PDF file.");
                                                            return;
                                                        }
                                                        
                                                        setTdsUploading(true);
                                                        try {
                                                            const res = await uploadProductTds(file);
                                                            set("tds_url")(res.url);
                                                        } catch (err: any) {
                                                            alert(err.message || "Failed to upload TDS file.");
                                                        } finally {
                                                            setTdsUploading(false);
                                                        }
                                                    }}
                                                    style={{ display: "none" }}
                                                    id="tds-file-upload"
                                                />
                                                <button
                                                    type="button"
                                                    disabled={tdsUploading}
                                                    onClick={() => document.getElementById("tds-file-upload")?.click()}
                                                    style={{
                                                        display: "flex", alignItems: "center", gap: 8,
                                                        padding: "10px 16px", borderRadius: 8,
                                                        border: "1px dashed var(--or)", background: "transparent",
                                                        color: "var(--or)", fontSize: 13, fontWeight: 500,
                                                        cursor: tdsUploading ? "not-allowed" : "pointer",
                                                        fontFamily: "'DM Sans', sans-serif", width: "100%",
                                                        justifyContent: "center", transition: "all 0.15s"
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!tdsUploading) {
                                                            e.currentTarget.style.background = "var(--or-dim)";
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.background = "transparent";
                                                    }}
                                                >
                                                    {tdsUploading ? "Uploading TDS PDF..." : "+ Upload TDS Document"}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </Field>
                                <Field label="Status" hint="Controls visibility on the website">
                                    <select
                                        className="pf-select"
                                        value={form.status}
                                        onChange={(e) => set("status")(e.target.value as FormState["status"])}
                                    >
                                        <option value="draft">Draft</option>
                                        <option value="published">Published</option>
                                        <option value="archived">Archived</option>
                                    </select>
                                </Field>
                            </CollapsibleSection>

                            {/* Section 3: Technical Specifications */}
                            <CollapsibleSection
                                title="3. Technical Specifications"
                                desc="Key performance traits (pH, temperature windows)"
                                badge={`${form.product_specifications.length} spec(s)`}
                                open={open[3]}
                                onToggle={() => setOpen((o) => ({ ...o, 3: !o[3] }))}
                            >
                                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1.6fr auto", gap: 8, padding: "0 12px" }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px" }}>Attribute</span>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px" }}>Value</span>
                                        <span style={{ width: 32 }} />
                                    </div>
                                    {form.product_specifications.map((spec) => (
                                        <SpecRow
                                            key={spec.id} spec={spec}
                                            onChange={(updated) => updateSpec(spec.id, updated)}
                                            onRemove={() => removeSpec(spec.id)}
                                            isOnly={form.product_specifications.length === 1}
                                        />
                                    ))}
                                </div>
                                <button type="button" onClick={addSpec} style={{
                                    display: "flex", alignItems: "center", gap: 6,
                                    width: "100%", padding: "10px", borderRadius: 9,
                                    border: "1px dashed rgba(191,61,12,0.3)", background: "transparent",
                                    color: "var(--or)", fontSize: 13, fontWeight: 500, cursor: "pointer",
                                    fontFamily: "'DM Sans', sans-serif"
                                }}>
                                    <span style={{ fontSize: 16 }}>+</span> Add Specification
                                </button>
                            </CollapsibleSection>

                            {/* Section 4: Packaging, Formats & Certs */}
                            <CollapsibleSection
                                title="4. Packaging, Formats & Certs"
                                desc="Select packaging configurations, product certificates, and top-level CTA details"
                                open={open[4]}
                                onToggle={() => setOpen((o) => ({ ...o, 4: !o[4] }))}
                            >
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                    <Field label="Formats Available" hint="Press enter/comma to register format">
                                        <TagInput
                                            values={form.formats}
                                            onChange={(v) => set("formats")(v)}
                                            placeholder="Add format (e.g. Liquid Concentrate)..."
                                        />
                                    </Field>
                                    <Field label="Certifications & Grades" hint="Press enter/comma to register tag">
                                        <TagInput
                                            values={form.certs}
                                            onChange={(v) => set("certs")(v)}
                                            placeholder="Add certification (e.g. Food Grade)..."
                                        />
                                    </Field>
                                </div>
                                <Field label="Top-Level Formats CTA Copy" hint="Paragraph below specifications list">
                                    <textarea
                                        className="pf-textarea"
                                        value={form.cta_copy}
                                        onChange={(e) => set("cta_copy")(e.target.value)}
                                        placeholder="Available in liquid and powder formats... Our technical team provides complete process optimization..."
                                        rows={3}
                                    />
                                </Field>
                            </CollapsibleSection>

                            {/* Section 5: Applications Repeater */}
                            <CollapsibleSection
                                title="5. Target Applications & Use-cases"
                                desc="Dynamic case-stories illustrating product usage in factories/industries"
                                badge={`${form.applications.length} Application(s)`}
                                open={open[5]}
                                onToggle={() => {
                                    setOpen((o) => ({ ...o, 5: !o[5] }));
                                    if (!open[5] && form.applications.length === 0) {
                                        addApplication();
                                    }
                                }}
                            >
                                <div className="repeater">
                                    {form.applications.map((app, index) => (
                                        <div key={app.id} className="rep-row">
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--border)", paddingBottom: 8, marginBottom: 8 }}>
                                                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--or)" }}>Use Case #{index + 1}</span>
                                                <button type="button" className="x" onClick={() => removeApplication(app.id)}>×</button>
                                            </div>

                                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                                <Field label="Application Kicker" required hint="Section tab title (e.g. Application — Bioethanol)">
                                                    <input
                                                        className="pf-input"
                                                        value={app.kicker}
                                                        onChange={(e) => updateApplication(app.id, { kicker: e.target.value })}
                                                        placeholder="Application — Bioethanol"
                                                    />
                                                </Field>
                                                <Field label="Heading Lines" hint="Two-line strong visual statement">
                                                    <div style={{ display: "flex", gap: 8 }}>
                                                        <input
                                                            className="pf-input"
                                                            value={app.heading[0] || ""}
                                                            onChange={(e) => updateApplication(app.id, { heading: [e.target.value, app.heading[1] || ""] })}
                                                            placeholder="Line 1 (e.g. More ethanol.)"
                                                        />
                                                        <input
                                                            className="pf-input"
                                                            value={app.heading[1] || ""}
                                                            onChange={(e) => updateApplication(app.id, { heading: [app.heading[0] || "", e.target.value] })}
                                                            placeholder="Line 2 (e.g. Same feedstock.)"
                                                        />
                                                    </div>
                                                </Field>
                                            </div>

                                            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
                                                <Field label="Emphasis Line" hint="Line index to italicize/orange (0 or 1)">
                                                    <select
                                                        className="pf-select"
                                                        value={app.headingEm}
                                                        onChange={(e) => updateApplication(app.id, { headingEm: Number(e.target.value) })}
                                                    >
                                                        <option value={0}>Line 1</option>
                                                        <option value={1}>Line 2</option>
                                                    </select>
                                                </Field>
                                                <Field label="Application Body Story" required hint="Elaborates on problem statement and solution benefits">
                                                    <textarea
                                                        className="pf-textarea"
                                                        value={app.body}
                                                        onChange={(e) => updateApplication(app.id, { body: e.target.value })}
                                                        placeholder="Explain conversion benefits or savings details..."
                                                        rows={3}
                                                    />
                                                </Field>
                                            </div>

                                            {/* Transformation Block */}
                                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                                <div style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
                                                    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", marginBottom: 6 }}>Transform From (Input)</div>
                                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                        <input
                                                            className="pf-input"
                                                            style={{ padding: "6px 10px", fontSize: 13 }}
                                                            value={app.transformFrom.label}
                                                            onChange={(e) => updateApplication(app.id, { transformFrom: { ...app.transformFrom, label: e.target.value } })}
                                                            placeholder="Label (e.g. Input)"
                                                        />
                                                        <textarea
                                                            className="pf-textarea"
                                                            style={{ padding: "6px 10px", fontSize: 13 }}
                                                            value={app.transformFrom.text}
                                                            onChange={(e) => updateApplication(app.id, { transformFrom: { ...app.transformFrom, text: e.target.value } })}
                                                            placeholder="State description..."
                                                            rows={2}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
                                                    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", marginBottom: 6 }}>Transform To (Output)</div>
                                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                        <input
                                                            className="pf-input"
                                                            style={{ padding: "6px 10px", fontSize: 13 }}
                                                            value={app.transformTo.label}
                                                            onChange={(e) => updateApplication(app.id, { transformTo: { ...app.transformTo, label: e.target.value } })}
                                                            placeholder="Label (e.g. Output)"
                                                        />
                                                        <textarea
                                                            className="pf-textarea"
                                                            style={{ padding: "6px 10px", fontSize: 13 }}
                                                            value={app.transformTo.text}
                                                            onChange={(e) => updateApplication(app.id, { transformTo: { ...app.transformTo, text: e.target.value } })}
                                                            placeholder="Result description..."
                                                            rows={2}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Metrics list */}
                                            <div>
                                                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", marginBottom: 6 }}>Impact Metrics</div>
                                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                    {app.metrics.map((m, mIdx) => (
                                                        <div key={mIdx} style={{ display: "flex", gap: 8 }}>
                                                            <input
                                                                className="pf-input"
                                                                style={{ padding: "6px 10px", flex: 1, fontSize: 13 }}
                                                                value={m.num}
                                                                onChange={(e) => updateApplication(app.id, {
                                                                    metrics: app.metrics.map((x, j) => j === mIdx ? { ...x, num: e.target.value } : x)
                                                                })}
                                                                placeholder="Value (e.g. 15%)"
                                                            />
                                                            <input
                                                                className="pf-input"
                                                                style={{ padding: "6px 10px", flex: 2, fontSize: 13 }}
                                                                value={m.label}
                                                                onChange={(e) => updateApplication(app.id, {
                                                                    metrics: app.metrics.map((x, j) => j === mIdx ? { ...x, label: e.target.value } : x)
                                                                })}
                                                                placeholder="Label (e.g. Higher yield)"
                                                            />
                                                            <button type="button" className="x" style={{ fontSize: 16 }} onClick={() => updateApplication(app.id, {
                                                                metrics: app.metrics.filter((_, j) => j !== mIdx)
                                                            })}>×</button>
                                                        </div>
                                                    ))}
                                                    <button type="button" className="rep-add" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => updateApplication(app.id, {
                                                        metrics: [...app.metrics, { num: "", label: "" }]
                                                    })}>+ Add Metric</button>
                                                </div>
                                            </div>

                                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                                <Field label="Buyers Header Label" hint="e.g. Who buys this">
                                                    <input
                                                        className="pf-input"
                                                        value={app.buyersLabel}
                                                        onChange={(e) => updateApplication(app.id, { buyersLabel: e.target.value })}
                                                        placeholder="Who buys this"
                                                    />
                                                </Field>
                                                <Field label="Buyers & Targets" hint="Press enter/comma to register buyer">
                                                    <TagInput
                                                        values={app.buyers}
                                                        onChange={(v) => updateApplication(app.id, { buyers: v })}
                                                        placeholder="Add buyer (e.g. Distilleries)..."
                                                    />
                                                </Field>
                                            </div>

                                            {/* Process Flow steps */}
                                            <div>
                                                {/* <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                                                    <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase" }}>Process Flow Pathway</span>
                                               
                                                </div> */}
                                                <Field label="Process Flow title" hint="e.g. Process in your plant">
                                                    <input
                                                        className="pf-input"
                                                        style={{ padding: "8px", fontSize: 12, height: "auto", marginBottom: "8px" }}
                                                        value={app.flowLabel}
                                                        onChange={(e) => updateApplication(app.id, { flowLabel: e.target.value })}
                                                        placeholder="Flow header label"
                                                    />
                                                </Field>                                          
                                                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                                    {app.flowSteps.map((fStep, fsIdx) => (
                                                        <div key={fsIdx} style={{
                                                            display: "flex", gap: 12, alignItems: "flex-start",
                                                            background: "var(--input-bg)", border: "1px solid var(--border)",
                                                            borderRadius: 10, padding: 14, boxShadow: "var(--shadow-sm)"
                                                        }}>
                                                            <div style={{
                                                                width: 24, height: 24, borderRadius: "50%",
                                                                background: "var(--or-dim)", border: "1px solid rgba(191,61,12,0.25)",
                                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                                fontSize: 11, fontWeight: 600, color: "var(--or)",
                                                                flexShrink: 0, marginTop: 6
                                                            }}>
                                                                {fsIdx + 1}
                                                            </div>
                                                            <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                                                                <input
                                                                    className="pf-input"
                                                                    style={{ padding: "8px 12px", fontSize: 13, background: "var(--surface)", height: "auto" }}
                                                                    value={fStep.title}
                                                                    onChange={(e) => updateApplication(app.id, {
                                                                        flowSteps: app.flowSteps.map((x, j) => j === fsIdx ? { ...x, title: e.target.value } : x)
                                                                    })}
                                                                    placeholder="Step Title (e.g. Feedstock preparation)"
                                                                />
                                                                <textarea
                                                                    className="pf-textarea"
                                                                    style={{ padding: "8px 12px", fontSize: 13, background: "var(--surface)", minHeight: 52 }}
                                                                    value={fStep.desc}
                                                                    onChange={(e) => updateApplication(app.id, {
                                                                        flowSteps: app.flowSteps.map((x, j) => j === fsIdx ? { ...x, desc: e.target.value } : x)
                                                                    })}
                                                                    placeholder="Step Description (e.g. Grain milled at 30-35% DS)"
                                                                    rows={2}
                                                                />
                                                            </div>
                                                            <button type="button" className="x" style={{ marginTop: 6 }} onClick={() => updateApplication(app.id, {
                                                                flowSteps: app.flowSteps.filter((_, j) => j !== fsIdx)
                                                            })}>×</button>
                                                        </div>
                                                    ))}
                                                    <button type="button" className="rep-add" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => updateApplication(app.id, {
                                                        flowSteps: [...app.flowSteps, { title: "", desc: "" }]
                                                    })}>+ Add Process Step</button>
                                                </div>
                                            </div>

                                            <Field label="Process Output / Final Result Text" hint="Footer highlight statement of target efficiency gains">
                                                <input
                                                    className="pf-input"
                                                    value={app.flowResultText}
                                                    onChange={(e) => updateApplication(app.id, { flowResultText: e.target.value })}
                                                    placeholder="e.g. Up to 12% more ethanol per tonne of feedstock"
                                                />
                                            </Field>
                                        </div>
                                    ))}
                                    <button type="button" className="rep-add" onClick={addApplication}>+ Add Application Story</button>
                                </div>
                            </CollapsibleSection>

                            {/* Section 6: SEO, Visibility & Buyer Journey (Collapsible block requested by user) */}
                            <CollapsibleSection
                                title="6. SEO, Information"
                                desc="Search metadata related products."
                                open={!!open[6]}
                                onToggle={() => setOpen((o) => ({ ...o, 6: !o[6] }))}
                            >
                                <Field
                                    label="Meta Title"
                                    required
                                    hint={`${p.metaTitle.length} / 60`}
                                >
                                    <input
                                        className={inputClass("metaTitle")}
                                        type="text"
                                        value={p.metaTitle}
                                        maxLength={60}
                                        onChange={(e) => update({ metaTitle: e.target.value })}
                                    />
                                </Field>
                                <Field
                                    label="Meta Description"
                                    required
                                    hint={`${p.metaDescription.length} / 160`}
                                >
                                    <textarea
                                        className={inputClass("metaDescription")}
                                        value={p.metaDescription}
                                        maxLength={160}
                                        onChange={(e) => update({ metaDescription: e.target.value })}
                                    />
                                </Field>



                            </CollapsibleSection>

                            {/* Form Submission Buttons */}
                            <div style={{
                                display: "flex", justifyContent: "flex-end", gap: 10,
                                paddingTop: 20, borderTop: "1px solid var(--border)",
                            }}>
                                <button type="button" onClick={handleReset} style={{
                                    padding: "10px 20px", borderRadius: 9, border: "1px solid var(--border)",
                                    background: "transparent", color: "var(--text2)", fontSize: 14, cursor: "pointer",
                                    fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s",
                                }}
                                    onMouseEnter={(e: MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
                                    onMouseLeave={(e: MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.background = "transparent"; }}
                                >Discard</button>
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={submitting}
                                    style={{
                                        padding: "10px 24px", borderRadius: 9,
                                        border: "1px solid rgba(191,61,12,0.35)",
                                        background: submitting ? "rgba(191,61,12,0.4)" : "linear-gradient(135deg, #BF3D0C, #E8593C)",
                                        color: "#fff", fontSize: 14, fontWeight: 500,
                                        cursor: submitting ? "not-allowed" : "pointer",
                                        fontFamily: "'DM Sans', sans-serif",
                                        boxShadow: "0 2px 12px rgba(191,61,12,0.25)",
                                        transition: "opacity 0.15s, transform 0.15s",
                                        opacity: submitting ? 0.7 : 1,
                                    }}
                                    onMouseEnter={(e: MouseEvent<HTMLButtonElement>) => { if (!submitting) { e.currentTarget.style.opacity = "0.88"; e.currentTarget.style.transform = "translateY(-1px)"; } }}
                                    onMouseLeave={(e: MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "translateY(0)"; }}
                                >
                                    {submitting ? "Saving…" : mode === "edit" ? "Save Changes" : "Publish Product"}
                                </button>
                            </div>
                        </div>

                        {/* Right column: sticky sidebar */}
                        <aside className="pf-sidebar" style={{ animation: "fadeSlideIn 0.4s ease 0.12s both" }}>
                            <SeoScorePanel form={form} />
                            <SerpPreviewPanel form={form} />
                            <QuickStatsPanel form={form} />
                        </aside>
                    </div>
                </div>
            </div>
        </>
    );
}
